import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { CdkDragDrop } from "@angular/cdk/drag-drop";

import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import SkillRating from "@skills-app/models/skillRating";

@Component({
  standalone: false,
  selector: "app-skill-chip-list",
  templateUrl: "./skill-chip-list.component.html",
  styleUrls: ["./skill-chip-list.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillChipListComponent {
  @Input() skills: ISkillNodeWithRating[] = [];
  @Input() showRating = true;
  @Input() dragDropEnabled = false;
  @Input() ordering = false;
  @Input() canReceiveDroppedSkills = false;
  @Input() clickActionCaption = "";
  @Input() noSkillsCaption?: string;

  @Input() azureId?: string;
  @Input() skillRating: SkillRating = SkillRating.Unknown;

  @Input() showRemove = false;
  @Input() removeAriaLabel = "Remove skill";

  private selectedIdSet = new Set<string>();

  @Input()
  set selectedSkillIds(value: Array<string | number> | null | undefined) {
    const arr = value ?? [];
    this.selectedIdSet = new Set(arr.map((x) => String(x)));
  }

  @Output() chipClick = new EventEmitter<[ISkillNodeWithRating, boolean]>();
  @Output() skillDropped = new EventEmitter<
    CdkDragDrop<ISkillNodeWithRating[]>
  >();
  @Output() skillRemove = new EventEmitter<ISkillNodeWithRating>();

  dragging = false;
  dragInProgress = false;

  onSkillDropped(event: CdkDragDrop<ISkillNodeWithRating[]>): void {
    this.dragInProgress = false;

    if (!this.dragDropEnabled) return;
    this.skillDropped.emit(event);
  }

  onDragStarted(): void {
    this.dragInProgress = true;
  }

  onDragEnded(): void {
    window.setTimeout(() => {
      this.dragInProgress = false;
    }, 0);
  }

  onChipClick(skill: ISkillNodeWithRating, event: MouseEvent): void {
    if (this.ordering || this.dragInProgress) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const multiSelect = event.ctrlKey || event.metaKey;
    this.chipClick.emit([skill, multiSelect]);
  }

  onRemoveClick(skill: ISkillNodeWithRating, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.skillRemove.emit(skill);
  }

  private getSelectionId(skill: ISkillNodeWithRating): string | number | null {
    const anySkill = skill as any;
    const id = anySkill?.SkillId ?? anySkill?.Id;
    if (typeof id === "string" || typeof id === "number") return id;
    return null;
  }

  isSelectedSkill(skill: ISkillNodeWithRating): boolean {
    const id = this.getSelectionId(skill);
    if (id === null) return false;
    return this.selectedIdSet.has(String(id));
  }

  trackBySkill(index: number, skill: ISkillNodeWithRating): string | number {
    const id = (skill as any)?.Id;
    if (typeof id === "string" || typeof id === "number") return id;

    const name = (skill as any)?.Name;
    if (typeof name === "string" && name.length > 0) return name;

    return index;
  }
}
